/**
 * Enhanced Notification Permission Modal
 * 
 * Features:
 * - Private/incognito browsing detection (handled by parent component)
 * - Desktop dismissal persistence via localStorage
 * - Mobile session-based dismissal
 * - Conditional display based on current permission status
 * - Different button behavior for mobile vs desktop
 * - Respects user's previous dismissal preferences
 */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import { useTranslation } from 'react-i18next';

interface NotificationPermissionModalProps {
  open: boolean;
  onRetry: () => void;
  onClose: () => void;
  onDismiss?: () => void;
  permission: NotificationPermission;
  isSupported: boolean;
  onRequestPermission: () => void;
  isMobile?: boolean;
}

function NotificationPermissionModal({
  open,
  onRetry,
  onClose,
  onDismiss,
  permission,
  isSupported,
  onRequestPermission,
  isMobile = false,
}: NotificationPermissionModalProps) {
  const { t } = useTranslation();

  if (!isSupported) {
    return (
      <Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <FuseSvgIcon color="error">heroicons-outline:exclamation-triangle</FuseSvgIcon>
            {t('NOTIFICATIONS_NOT_SUPPORTED_TITLE')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {t('NOTIFICATIONS_NOT_SUPPORTED_MESSAGE')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onDismiss || onClose} variant="outlined">
            {t('CONTINUE_WITHOUT_NOTIFICATIONS')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} maxWidth="md" fullWidth disableEscapeKeyDown>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <FuseSvgIcon color="primary">heroicons-outline:bell</FuseSvgIcon>
          {t('ENABLE_NOTIFICATIONS_TITLE')}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{t('NOTIFICATIONS_REQUIRED_WARNING')}</strong>
          </Typography>
        </Alert>

        <Typography variant="body1" gutterBottom>
          {t('NOTIFICATIONS_REQUIRED_DESCRIPTION')}
        </Typography>

        <List dense>
          <ListItem>
            <ListItemIcon>
              <FuseSvgIcon>heroicons-outline:home</FuseSvgIcon>
            </ListItemIcon>
            <ListItemText primary={t('ROOM_ASSIGNMENTS_UPDATES')} />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <FuseSvgIcon>heroicons-outline:wrench</FuseSvgIcon>
            </ListItemIcon>
            <ListItemText primary={t('MAINTENANCE_UPDATES')} />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <FuseSvgIcon>heroicons-outline:clock</FuseSvgIcon>
            </ListItemIcon>
            <ListItemText primary={t('EXPRESS_CLEANING_UPDATES')} />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <FuseSvgIcon>heroicons-outline:user-group</FuseSvgIcon>
            </ListItemIcon>
            <ListItemText primary={t('TEAM_COMMUNICATIONS')} />
          </ListItem>
        </List>

        {permission === 'denied' && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>{t('NOTIFICATIONS_BLOCKED_TITLE')}.</strong> {t('NOTIFICATIONS_BLOCKED_MESSAGE')}
            </Typography>
          </Alert>
        )}

        {permission === 'default' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              {t('NOTIFICATIONS_PERMISSION_INFO')}
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {permission === 'denied' ? (
          <>
            <Button onClick={onRequestPermission} variant="contained" color="primary">
              {t('ALLOW_NOTIFICATIONS')}
            </Button>
            <Button onClick={onRetry} variant="outlined">
              {t('IVE_ENABLED_NOTIFICATIONS')}
            </Button>
            {/* Add dismiss option for denied permissions on mobile */}
            {isMobile && (
              <Button onClick={onDismiss || onClose} variant="text" color="inherit" size="small">
                {t('DONT_ASK_AGAIN')}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button onClick={onDismiss || onClose} variant="outlined" color="warning">
              {isMobile ? t('NOT_NOW') : t('SKIP_NOT_RECOMMENDED')}
            </Button>
            <Button
              onClick={onRequestPermission}
              variant="contained"
              color="primary"
              startIcon={<FuseSvgIcon>heroicons-outline:bell</FuseSvgIcon>}
            >
              {t('ALLOW_NOTIFICATIONS')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default NotificationPermissionModal;